/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ColumnFilterElementTemplateOptions,
  ColumnProps,
  ColumnSortEvent,
} from 'primereact/column';
import YesNoIndicator from '@/components/YesNoIndicator/YesNoIndicator';
import { formatDateAndTime } from '@/utils/formatDate';

import {
  InputDateFilter,
  TableDropdownFilter,
  TruncatedTextWithTooltip,
} from '@/components';
import { TableData } from '@/components/Table/types';
import { SolasInfo } from '../types';
import EditVGM from './EditVGM/EditVGM';

export type ParsedSolasInfo = Omit<
  SolasInfo,
  'tipoContenedor' & 'cierreStacking'
> & {
  tipo?: string;
  tamano?: string;
};

export const getTableColumns = (
  data: ParsedSolasInfo[] | undefined
): ColumnProps[] => {
  return [
    {
      field: 'buque',
      header: 'Buque',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      sortFunction: (e: ColumnSortEvent) => {
        let dataSort = e.data.sort((data1: any, data2: any) => {
          const value1 = data1[e.field] as string;
          const value2 = data2[e.field] as string;
          return value1.localeCompare(value2, 'es', { sensitivity: 'base' });
        });

        if (e.order === -1) {
          dataSort = e.data.reverse();
        }
        return dataSort;
      },
      body: (rowData) =>
        rowData.buque.length > 30 ? (
          <TruncatedTextWithTooltip text={rowData.buque} maxLength={30} />
        ) : (
          rowData.buque
        ),
    },
    {
      field: 'booking',
      header: 'Booking',
      filter: true,
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'contenedor',
      header: 'Contenedor',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) => <b>{rowData.contenedor}</b>,
    },
    {
      field: 'estadoVGM',
      header: 'VGM',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) => <YesNoIndicator value={rowData.estadoVGM} />,
      filterElement: (options: ColumnFilterElementTemplateOptions) => (
        <TableDropdownFilter
          options={options}
          data={data as TableData}
          fieldName="estadoVGM"
        />
      ),
    },
    {
      field: 'pesoDeclarado',
      header: 'VGM declarado (Kg)',
      filter: false,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) => (
        <EditVGM
          currentVGMnumber={rowData.pesoDeclarado}
          cierreStacking={rowData.cierreStacking}
          oidExport={rowData.oidExport}
        />
      ),
      style: { minWidth: '14rem', maxWidth: '14rem' },
    },
    {
      field: 'peso',
      header: 'Peso (Kg)',
      filter: true,
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'armador',
      header: 'Armador',
      filter: true,
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'playa',
      header: 'Playa',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      filterElement: (options: ColumnFilterElementTemplateOptions) => (
        <TableDropdownFilter
          options={options}
          data={data as TableData}
          fieldName="playa"
        />
      ),
    },

    {
      field: 'tipo',
      header: 'Ti',
      filter: true,
      filterElement: (options: ColumnFilterElementTemplateOptions) => (
        <TableDropdownFilter
          options={options}
          data={data as TableData}
          fieldName="tipo"
        />
      ),
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'tamano',
      header: 'Ta',
      filter: true,
      filterElement: (options: ColumnFilterElementTemplateOptions) => (
        <TableDropdownFilter
          options={options}
          data={data as TableData}
          fieldName="tamano"
        />
      ),
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'statusout',
      header: 'Statusout',
      filter: true,
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'exportador',
      header: 'Exportador',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) =>
        (rowData.exportador ?? '').length > 30 ? (
          <TruncatedTextWithTooltip text={rowData.exportador} maxLength={30} />
        ) : (
          rowData.exportador
        ),
    },

    {
      field: 'mercaderia',
      header: 'Mercadería',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) =>
        (rowData.mercaderia ?? '').length > 30 ? (
          <TruncatedTextWithTooltip text={rowData.mercaderia} maxLength={30} />
        ) : (
          rowData.mercaderia
        ),
    },
    {
      field: 'destino',
      header: 'Puerto Descarga',
      filter: true,
      showFilterMenu: true,
      sortable: true,
    },
    {
      field: 'cierreStacking',
      header: 'Cierre Stacking',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) => formatDateAndTime(rowData.cierreStacking),
      filterElement: (options: ColumnFilterElementTemplateOptions) => (
        <InputDateFilter options={options} />
      ),
      filterField: 'cierreStacking',
      dataType: 'date',
      headerStyle: { minWidth: '12rem' },
    },
    {
      field: 'resultadoProceso',
      header: 'Resultado VERMAS',
      filter: true,
      showFilterMenu: true,
      sortable: true,
    },

    {
      field: 'pendiente',
      header: 'Pendiente',
      filter: true,
      showFilterMenu: true,
      sortable: true,
      body: (rowData) => (
        <YesNoIndicator value={rowData.pendiente} showIcon={false} />
      ),
      filterElement: (options: ColumnFilterElementTemplateOptions) => (
        <TableDropdownFilter
          options={options}
          data={data as TableData}
          fieldName="estadoVGM"
        />
      ),
    },
  ];
};
